import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { leadAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Phone, Mail, MapPin, Calendar, Clock, 
  ArrowUpRight, GripVertical, Users, AlertCircle, Sparkles
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export function LeadsPipelinePage() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [pipeline, setPipeline] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);
  // Pipeline-level form_type filter. Defaults to "all" so the kanban
  // behaves exactly as before for any user who never opens the dropdown.
  const [formType, setFormType] = useState('all');

  const PIPELINE_COLUMNS = [
    { id: 'new', label: t('status.new'), color: 'bg-blue-500' },
    { id: 'contacted', label: t('status.contacted'), color: 'bg-yellow-500' },
    { id: 'appointment', label: t('status.appointment'), color: 'bg-purple-500' },
    { id: 'quote_sent', label: t('status.quote_sent'), color: 'bg-orange-500' },
    { id: 'won', label: t('status.won'), color: 'bg-green-500' },
    { id: 'lost', label: t('status.lost'), color: 'bg-red-500' },
  ];

  const FORM_TYPE_OPTIONS = [
    { value: 'all',           label: language === 'fr' ? 'Tous les types'   : 'All types' },
    { value: 'standard',      label: language === 'fr' ? 'Standards'        : 'Standard' },
    { value: 'ai_estimation', label: language === 'fr' ? '🤖 IA Estimation' : '🤖 AI Estimation' },
  ];

  // Refresh on every route entry (covers /leads -> /pipeline navigation
  // and back/forward) so a lead deleted elsewhere cannot linger in a
  // stale local state snapshot.
  useEffect(() => {
    loadPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, formType]);

  // Refresh when the tab regains visibility (bfcache restore, multi-tab
  // workflows). Listener is registered ONCE; loadPipeline is stable
  // enough to call directly without effecting infinite loops.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadPipeline();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const loadPipeline = async () => {
    try {
      const params = formType !== 'all' ? { form_type: formType } : {};
      const response = await leadAPI.getPipeline(params);
      setPipeline(response.data);
    } catch (err) {
      console.error('Error loading pipeline:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (lead, fromStatus) => {
    setDraggedLead({ lead, fromStatus });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (toStatus) => {
    if (!draggedLead || draggedLead.fromStatus === toStatus) {
      setDraggedLead(null);
      return;
    }

    const { lead, fromStatus } = draggedLead;

    // Optimistic update
    setPipeline(prev => ({
      ...prev,
      [fromStatus]: prev[fromStatus].filter(l => l.lead_id !== lead.lead_id),
      [toStatus]: [{ ...lead, status: toStatus }, ...prev[toStatus]],
    }));

    try {
      await leadAPI.update(lead.lead_id, { status: toStatus });
    } catch (err) {
      console.error('Error updating lead:', err);
      // Revert on error
      loadPipeline();
    }

    setDraggedLead(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (hours < 1) return t('leads.justNow');
    if (hours < 24) return t('leads.hoursAgo', { hours });
    if (days < 7) return t('leads.daysAgo', { days });
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalLeads = Object.values(pipeline).reduce((sum, leads) => sum + leads.length, 0);

  return (
    <div className="space-y-6" data-testid="leads-pipeline-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">{t('leads.pipelineTitle')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('leads.pipelineDesc')}
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <Select value={formType} onValueChange={setFormType}>
            <SelectTrigger className="w-[180px]" data-testid="pipeline-form-type-filter">
              <SelectValue placeholder={language === 'fr' ? 'Type' : 'Type'} />
            </SelectTrigger>
            <SelectContent>
              {FORM_TYPE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`pipeline-form-type-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button asChild variant="outline">
            <Link to="/leads">
              {t('leads.listView')}
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Pipeline Kanban */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {PIPELINE_COLUMNS.map((column) => (
            <div
              key={column.id}
              className="w-80 flex-shrink-0"
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(column.id)}
            >
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${column.color}`} />
                      <CardTitle className="text-base font-bold">
                        {column.label}
                      </CardTitle>
                    </div>
                    <Badge variant="secondary" className="font-mono">
                      {pipeline[column.id]?.length || 0}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="space-y-3 pr-4">
                      {pipeline[column.id]?.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                          <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t('leads.noLeadInColumn')}</p>
                        </div>
                      ) : (
                        pipeline[column.id]?.map((lead) => (
                          <div
                            key={lead.lead_id}
                            className="kanban-card group"
                            draggable
                            onDragStart={() => handleDragStart(lead, column.id)}
                          >
                            <div className="flex items-start gap-2">
                              <GripVertical className="h-4 w-4 text-muted-foreground/50 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="font-semibold truncate flex items-center gap-2">
                                    {lead.name}
                                    {lead.is_urgent && (
                                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" title={t('leads.urgent')} />
                                    )}
                                    {lead.form_type === 'ai_estimation' && (
                                      <Badge
                                        variant="outline"
                                        className="text-[9px] border-amber-500/50 text-amber-700 bg-amber-500/10 gap-1 font-semibold whitespace-nowrap px-1.5"
                                        data-testid={`pipeline-ai-badge-${lead.lead_id}`}
                                      >
                                        <Sparkles className="h-2.5 w-2.5" />
                                        IA
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                
                                {lead.service && (
                                  <Badge variant="outline" className="mb-2 text-xs">
                                    {lead.service}
                                  </Badge>
                                )}
                                
                                <div className="space-y-1 text-sm text-muted-foreground">
                                  {lead.phone && (
                                    <p className="flex items-center gap-2">
                                      <Phone className="h-3 w-3" />
                                      {lead.phone}
                                    </p>
                                  )}
                                  {lead.city && (
                                    <p className="flex items-center gap-2">
                                      <MapPin className="h-3 w-3" />
                                      {lead.city}
                                    </p>
                                  )}
                                </div>
                                
                                <div className="flex items-center justify-between mt-3 pt-3 border-t">
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(lead.created_at)}
                                  </span>
                                  <Button asChild variant="ghost" size="sm" className="h-7 px-2">
                                    <Link to={`/leads/${lead.lead_id}`}>
                                      {t('common.view')}
                                    </Link>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
